import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Fireworks Canvas Component
function FireworksCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let fireworks = [];
            let particles = [];
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // Helper: Create Firework Object
            const createFirework = () => ({
                x: Math.random() * canvas.width,
                y: canvas.height,
                targetY: Math.random() * canvas.height * 0.4 + canvas.height * 0.1,
                speed: Math.random() * 3 + 5,
                radius: 3,
                color: `hsl(${Math.random() * 360}, 100%, 60%)`,
                trail: [],
                trailLength: 10,
            });

            // Helper: Update Firework
            const updateFirework = (fw) => {
                fw.trail.push({ x: fw.x, y: fw.y });
                if (fw.trail.length > fw.trailLength) {
                    fw.trail.shift();
                }

                fw.y -= fw.speed;

                return fw.y <= fw.targetY;
            };

            // Helper: Draw Firework
            const drawFirework = (fw) => {
                fw.trail.forEach((pos, index) => {
                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, fw.radius * (index / fw.trailLength), 0, Math.PI * 2);
                    ctx.fillStyle = fw.color;
                    ctx.globalAlpha = index / fw.trailLength;
                    ctx.fill();
                });

                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.arc(fw.x, fw.y, fw.radius, 0, Math.PI * 2);
                ctx.fillStyle = fw.color;
                ctx.fill();
            };

            // Helper: Create Particle Object
            const createParticle = (x, y, color) => ({
                x,
                y,
                radius: Math.random() * 2 + 1,
                color,
                velocity: {
                    x: (Math.random() - 0.5) * 8,
                    y: (Math.random() - 0.5) * 8,
                },
                gravity: 0.08,
                friction: 0.98,
                alpha: 1,
                decay: Math.random() * 0.015 + 0.01,
            });

            // Helper: Update Particle
            const updateParticle = (p) => {
                p.velocity.x *= p.friction;
                p.velocity.y *= p.friction;
                p.velocity.y += p.gravity;

                p.x += p.velocity.x;
                p.y += p.velocity.y;

                p.alpha -= p.decay;
            };

            // Helper: Draw Particle
            const drawParticle = (p) => {
                ctx.save();
                ctx.globalAlpha = p.alpha;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fillStyle = p.color;
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Text Particle Object
            const createTextParticle = (x, y, char, color) => ({
                x,
                y,
                char,
                color,
                velocity: {
                    x: (Math.random() - 0.5) * 4,
                    y: (Math.random() - 0.5) * 4,
                },
                gravity: 0.05,
                friction: 0.99,
                alpha: 1,
                decay: 0.008,
                scale: 1,
            });

            // Helper: Update Text Particle
            const updateTextParticle = (tp) => {
                tp.velocity.x *= tp.friction;
                tp.velocity.y *= tp.friction;
                tp.velocity.y += tp.gravity;

                tp.x += tp.velocity.x;
                tp.y += tp.velocity.y;

                tp.alpha -= tp.decay;
                tp.scale = Math.max(0.3, tp.alpha);
            };

            // Helper: Draw Text Particle
            const drawTextParticle = (tp) => {
                ctx.save();
                ctx.globalAlpha = tp.alpha;
                ctx.font = `bold ${40 * tp.scale}px Arial`;
                ctx.fillStyle = tp.color;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(tp.char, tp.x, tp.y);
                ctx.restore();
            };

            // Create text explosion
            const createTextExplosion = (x, y) => {
                const text = 'HAPPY DIWALI';
                const colors = ['#FF6B35', '#F7931E', '#FDC830', '#FF0844', '#FFB344'];

                const charSpacing = 30;
                const startX = x - (text.length * charSpacing) / 2;

                for (let i = 0; i < text.length; i += 1) {
                    const char = text[i];
                    const charX = startX + i * charSpacing;
                    const charY = y;
                    const color = colors[Math.floor(Math.random() * colors.length)];

                    textParticles.push(createTextParticle(charX, charY, char, color));
                }
            };

            // Create explosion
            const createExplosion = (x, y, color) => {
                const particleCount = 80;
                for (let i = 0; i < particleCount; i += 1) {
                    particles.push(createParticle(x, y, color));
                }

                // 20% chance to create "HAPPY DIWALI" text
                if (Math.random() < 0.2) {
                    createTextExplosion(x, y);
                }
            };

            // Animation loop
            const animate = () => {
                ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Random firework launch
                if (Math.random() < 0.05) {
                    fireworks.push(createFirework());
                }

                // Update and draw fireworks
                fireworks = fireworks.filter((fw) => {
                    drawFirework(fw);
                    const shouldExplode = updateFirework(fw);
                    if (shouldExplode) {
                        createExplosion(fw.x, fw.y, fw.color);
                        return false;
                    }
                    return true;
                });

                // Update and draw particles
                particles = particles.filter((p) => {
                    drawParticle(p);
                    updateParticle(p);
                    return p.alpha > 0;
                });

                // Update and draw text particles
                textParticles = textParticles.filter((tp) => {
                    drawTextParticle(tp);
                    updateTextParticle(tp);
                    return tp.alpha > 0;
                });

                animationFrameId = requestAnimationFrame(animate);
            };

            animate();

            // Cleanup
            return () => {
                window.removeEventListener('resize', resizeCanvas);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

export default function DiwaliTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
                px: { xs: 2, md: 12 },
                overflow: 'hidden',
                background: '#0A0010',
            }}
        >
            <FireworksCanvas />

            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(20,8,0,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha('#FF6B00', 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha('#FF6B00', 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: 'linear-gradient(90deg, transparent, #FF6B00, #FFD700, #FF6B00, transparent)',
                    borderRadius: '0 0 4px 4px',
                    animation: 'diwaliGlow 3s ease-in-out infinite',
                },
                '@keyframes diwaliGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: 'linear-gradient(90deg, #FF6B00, #FFD700, #FF6B00)',
                        backgroundSize: '200% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#3A1800', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha('#FF6B00', 0.4)}`,
                    }}>🪔 &nbsp; Happy Diwali &nbsp; 🪔</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2 }}><Logo /></Stack>
                {cloneElement(children, { hideHelpLink: true })}
            </Card>

            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha('#FF6B00', 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#3A1800', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

DiwaliTheme.propTypes = {
    children: PropTypes.node,
};
